// Paste.ts
import { Extension } from '@tiptap/core'

export type PasteOptions = {
  onError?: (err: unknown) => void
  // Keyboard shortcuts: set to false to disable default bindings
  shortcuts?: {
    pasteHtml: string | false
    pastePlain: string | false
  }
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    pasteExt: {
      paste: () => ReturnType
      pastePlain: () => ReturnType
    }
  }
}

export const Paste = Extension.create<PasteOptions>({
  name: 'imperia-paste',

  addOptions() {
    return {
      onError: undefined,
      shortcuts: {
        pasteHtml: false,        // Ctrl/Cmd + V
        pastePlain: false, // Ctrl/Cmd + Shift + V
      },
    }
  },

  addCommands() {
    return {
      paste:
        () =>
        ({ editor }) => {
          editor.chain().focus().run()

          const readFn = async () => {
            // Prefer navigator.clipboard.read() for HTML
            if (navigator.clipboard && 'read' in navigator.clipboard) {
              const items = await navigator.clipboard.read()
              for (const item of items) {
                if (item.types?.includes('text/html')) {
                  const blob = await item.getType('text/html')
                  let html = await blob.text()
                  if (html?.trim()) {
                    editor.chain().focus().insertContent(html).run()
                    return
                  }
                }
              }
              // Fallback: plain text from the first item if available
              const first = items[0]
              if (first && itemHasText(first)) {
                const textBlob = await first.getType('text/plain')
                const text = await textBlob.text()
                if (text?.trim()) {
                  editor.chain().focus().insertContent(text).run()
                }
                return
              }
            }

            // Ultimate fallback: readText()
            if (navigator.clipboard?.readText) {
              const text = await navigator.clipboard.readText()
              if (text?.trim()) {
                editor.chain().focus().insertContent(text).run()
              }
            }
          }

          readFn().catch(err => {
            this.options.onError?.(err)
            console.error('Clipboard paste failed:', err)
          })

          return true
        },

      pastePlain:
        () =>
        ({ editor }) => {
          editor.chain().focus().run()

          const readPlain = async () => {
            if (navigator.clipboard?.readText) {
              const text = await navigator.clipboard.readText()
              if (text?.trim()) {
                editor.chain().focus().insertContent(text).run()
              }
            } else if (navigator.clipboard && 'read' in navigator.clipboard) {
              const items = await navigator.clipboard.read()
              const first = items[0]
              if (first && itemHasText(first)) {
                const blob = await first.getType('text/plain')
                const text = await blob.text()
                if (text?.trim()) {
                  editor.chain().focus().insertContent(text).run()
                }
              }
            }
          }

          readPlain().catch(err => {
            this.options.onError?.(err)
            console.error('Clipboard plain paste failed:', err)
          })

          return true
        },
    }
  },

  addKeyboardShortcuts() {
    const map: Record<string, () => boolean> = {}

    if (this.options.shortcuts?.pasteHtml) {
      map[this.options.shortcuts.pasteHtml] = () =>
        this.editor.commands.paste()
    }

    if (this.options.shortcuts?.pastePlain) {
      map[this.options.shortcuts.pastePlain] = () =>
        this.editor.commands.pastePlain()
    }

    return map
  },
})

function itemHasText(item: ClipboardItem): boolean {
  return Array.isArray(item.types) && item.types.includes('text/plain')
}
